import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/lib/store/auth'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import type { SubscriptionTier } from '@mindweb/shared'

interface PricingCardProps {
  readonly tierKey: string
  readonly name: string
  readonly price: number
  readonly maxGraphs: number
  readonly maxNodesPerGraph: number
  readonly highlighted?: boolean
  readonly currentTier?: SubscriptionTier
  readonly cancelAtPeriodEnd?: boolean
  readonly pendingTier?: SubscriptionTier | null
  readonly onAction?: () => void
}

type ButtonLabelKey = '' | 'currentPlan' | 'reactivate' | 'subscribe'

type ButtonState = {
  readonly labelKey: ButtonLabelKey
  readonly action: 'none' | 'checkout' | 'reactivate'
  readonly disabled: boolean
  readonly variant: 'default' | 'secondary'
}

function getButtonState(
  tierKey: string,
  price: number,
  currentTier: SubscriptionTier,
  cancelAtPeriodEnd: boolean,
): ButtonState {
  const isFree = price === 0

  if (isFree) {
    return currentTier === 'free'
      ? { labelKey: 'currentPlan', action: 'none', disabled: true, variant: 'secondary' }
      : { labelKey: '', action: 'none', disabled: true, variant: 'secondary' }
  }

  if (tierKey === currentTier) {
    if (cancelAtPeriodEnd) {
      return { labelKey: 'reactivate', action: 'reactivate', disabled: false, variant: 'default' }
    }
    return { labelKey: 'currentPlan', action: 'none', disabled: true, variant: 'secondary' }
  }

  if (currentTier === 'free') {
    return { labelKey: 'subscribe', action: 'checkout', disabled: false, variant: 'default' }
  }

  return { labelKey: 'subscribe', action: 'checkout', disabled: false, variant: 'default' }
}

export function PricingCard({
  tierKey,
  name,
  price,
  maxGraphs,
  maxNodesPerGraph,
  highlighted,
  currentTier = 'free',
  cancelAtPeriodEnd = false,
  onAction,
}: PricingCardProps) {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { t } = useTranslation()

  const buttonState = getButtonState(tierKey, price, currentTier, cancelAtPeriodEnd)

  const buttonLabel = buttonState.labelKey === 'currentPlan'
    ? t('billing.currentPlan')
    : buttonState.labelKey === 'reactivate'
      ? t('billing.reactivate')
      : buttonState.labelKey === 'subscribe'
        ? t('billing.subscribe')
        : ''

  const handleClick = async () => {
    if (price === 0) {
      navigate(isAuthenticated ? '/dashboard' : '/login')
      return
    }

    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    if (buttonState.disabled || buttonState.action === 'none') return

    setLoading(true)
    try {
      if (buttonState.action === 'checkout') {
        const result = await api.post<{ url: string }>('/api/billing/checkout', {
          tier: tierKey,
        })
        if (result.success && result.data) {
          window.location.href = result.data.url
        }
        return
      }

      if (buttonState.action === 'reactivate') {
        await api.post('/api/billing/reactivate')
        onAction?.()
      }
    } finally {
      setLoading(false)
    }
  }

  const nodesLabel = maxNodesPerGraph === Infinity
    ? t('billing.nodesPerGraph_unlimited')
    : t('billing.nodesPerGraph_count', { count: maxNodesPerGraph })

  const features = [
    maxGraphs === Infinity ? t('billing.graphs_unlimited') : t('billing.graphs_count', { count: maxGraphs }),
    nodesLabel,
    t('billing.deepResearchReports'),
    t('billing.interactiveQA'),
    t('billing.knowledgeGraphCanvas'),
  ]

  return (
    <div
      className={`rounded-xl border p-6 ${
        highlighted
          ? 'border-brand-500 bg-brand-500/5 shadow-lg shadow-brand-500/10'
          : 'border-border bg-card'
      }`}
    >
      <h3 className="text-lg font-semibold text-foreground">{name}</h3>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-3xl font-bold text-foreground">
          {price === 0 ? t('billing.free') : `$${price}`}
        </span>
        {price > 0 && <span className="text-sm text-muted-foreground">{t('billing.perMonth')}</span>}
      </div>

      <ul className="mt-6 space-y-3">
        {features.map((feature) => (
          <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
            <Check className="h-4 w-4 text-green-400 shrink-0" />
            {feature}
          </li>
        ))}
      </ul>

      {buttonLabel && (
        <Button
          variant={buttonState.variant === 'default' && highlighted ? 'default' : buttonState.variant}
          className="mt-6 w-full"
          onClick={handleClick}
          loading={loading}
          disabled={buttonState.disabled}
        >
          {buttonLabel}
        </Button>
      )}
    </div>
  )
}
